import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Label, FormGroup } from 'reactstrap';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import useUser from '../../../customHooks/useUser';
import { Button } from 'reactstrap';
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalFooter } from 'reactstrap';
import moment from 'moment';
import { ButtonGroup } from '@material-ui/core';
import { AircraftType } from '../../../globals/interfaces';
import { AircraftTypeSchema, AircraftTypeSchemaUpdate } from './validation';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import BackButton from '../../../components/custom/BackButton';
import Input from '../../../components/Input';
import { ZodFormProvider, useZodForm } from '../../../components/custom/ZodForm/ZodFormProvider';
import { createZodInput } from '../../../components/custom/ZodForm/TypedInput';
import ActionIcon from '../../../components/custom/ActionIcon';

const FormInput = createZodInput<AircraftType>();

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    record?: AircraftType;
    onSubmit: (data: AircraftType, update?: boolean) => void;
    deleteItem: (id: number) => Promise<void>;
}

export default function AircraftTypesModal({
    modal,
    setModal,
    record,
    onSubmit,
    deleteItem
}: Props) {

    const { user } = useUser();
    const update = Boolean(record);

    // Create form with strict validation options
    const methods = useZodForm<AircraftType>({
        resolver: update ? zodResolver(AircraftTypeSchemaUpdate) : zodResolver(AircraftTypeSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldFocusError: true,
        // Ensure number fields are properly converted
        context: { valueAsNumber: true },
    });

    const {
        handleSubmit,
        reset,
        clearErrors,
        trigger,
        formState: { errors, isValid, isDirty },
        watch,
        setValue
    } = methods;

    // Reset form when modal opens/closes or record changes
    useEffect(() => {
        if (modal) {
            // Clear all previous errors
            clearErrors();
            const now = getTsDate();

            if (record) {
                reset({
                    ...record,
                    modified: now,
                    modifiedBy: user.s_email
                });
            } else {
                reset({
                    FAA_Designator: '', // I had do declare the field, otherwise, the validation was not triggering for it, it was just showing ""
                    Manufacturer: '',
                    Model_FAA: '',
                    Model_BADA: '',
                    Physical_Class_Engine: '',
                    Num_Engines: undefined,
                    MTOW: undefined,
                    MALW: undefined,
                    ICAO_WTC: '',
                    Class: '',
                    FAA_Weight: '',
                    created: now,
                    createdBy: user.s_email,
                    modified: now,
                    modifiedBy: user.s_email
                });
            }

            trigger();
        }
    }, [modal, record, reset, clearErrors, user.s_email]);

    const toggle = () => setModal(!modal);

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <div>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} Aircraft Type</h4>
                    </div>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <ZodFormProvider methods={methods}>
                    <FlexContainer>
                        <div>
                            <FormInput label="FAA Designator" name={'FAA_Designator'} />
                            <FormInput label="Manufacturer" name={'Manufacturer'} required />
                            <FormInput label="Model_FAA" name={'Model_FAA'} required />
                            <FormInput label="Model_BADA" name={'Model_BADA'} required />
                            <FormInput label="Physical_Class_Engine" name={'Physical_Class_Engine'} required />
                        </div>
                        <div>
                            <FormInput label="Num_Engines" name={'Num_Engines'} type={'number'} required />
                            <FormInput label="MTOW" name={'MTOW'} type={'number'} />
                            <FormInput label="MALW" name={'MALW'} type={'number'} />
                            <FormInput label="ICAO_WTC" name={'ICAO_WTC'} />
                            <FormInput label="Class" name={'Class'} />
                            <FormInput label="FAA_Weight" name={'FAA_Weight'} />
                        </div>
                    </FlexContainer>
                </ZodFormProvider>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label className={'d-block'}>
                        {createdUpdatedInfo(record, true)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteItem(record.id)}
                                    simulateLoading
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(data => onSubmit(data, update))}
                            simulateLoading
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </CustomModal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    & div {
        display: flex;
        align-items: center;
    }
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    width: 100%;

    & div {
        width: 100%;
    }
`;

const FooterFlex = styled.div`
    display: flex;
`;

const CustomModal = styled(Modal)`
    width: 100%;
    max-width: 1000px;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;